import { useEffect, useMemo, useRef, useState } from "react";
import { useRegistration } from "@/context/RegistrationContext";
import { NATIONALITIES, UGC_UNIVERSITY_INFO, BLOOD_GROUPS, GENDERS } from "@/lib/constants";
import { Info, UploadCloud, ArrowLeft, ArrowRight, User, ExternalLink, IdCard, Users2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const readAsDataURL = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

// Numeric-only phone input handler — strips anything non-digit and enforces 10-15 length.
const onlyDigits = (v, max = 15) => (v || "").replace(/\D+/g, "").slice(0, max);

const phoneValid = (p) => /^\d{10,15}$/.test((p || "").trim());
const pincodeValid = (p) => /^\d{4,10}$/.test((p || "").trim());

const REQ = "This field is required";
const PHONE_REQ = "Enter a valid phone (10–15 digits)";
const PIN_REQ = "Enter a valid PIN / ZIP code";
const MAX_DOB = "2011-01-01";
const DOB_REQ = "Date of birth must be on or before 01/01/2011";

const RequiredStar = () => <span className="text-red-500 ml-1">*</span>;

const ErrText = ({ showErrors, errors, k }) =>
  showErrors && errors[k] ? (
    <div className="flex items-center gap-1 text-[11px] text-red-600 mt-1" data-testid={`err-${k}`}>
      <AlertCircle className="h-3 w-3" /> {errors[k]}
    </div>
  ) : null;

export default function CandidateDetailsStep({ onNext, onBack }) {
  const { state, updateCandidate } = useRegistration();
  const c = state.candidate;
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [dobFocused, setDobFocused] = useState(false);
  const accuracyConfirmed = !!c.accuracyConfirmed;
  const setAccuracyConfirmed = (v) => updateCandidate({ accuracyConfirmed: v });

  const promptAccuracyConfirm = () =>
    toast.error("Please check the box above confirming you'll enter your details exactly as per your certificate before editing your name or photo.");
  const pinResolved = useRef(""); // guard against duplicate lookups for the same pin

  // Pincode → City / State auto-fill via postalpincode.in (free, no key needed).
  useEffect(() => {
    const pin = (c.pincode || "").trim();
    if (!/^\d{6}$/.test(pin)) return;
    if (pinResolved.current === pin) return;
    let cancelled = false;
    (async () => {
      setPinLoading(true);
      try {
        const r = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await r.json();
        const row = Array.isArray(data) ? data[0] : null;
        const office = row?.PostOffice?.[0];
        if (!cancelled && office) {
          pinResolved.current = pin;
          const patch = {};
          // Only fill if the candidate hasn't typed anything (don't overwrite).
          if (!c.city) patch.city = office.District || office.Name || "";
          if (!c.state) patch.state = office.State || "";
          if (Object.keys(patch).length) updateCandidate(patch);
        }
      } catch (_e) {
        // Silent fail — user can still fill manually.
      } finally {
        if (!cancelled) setPinLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.pincode]);

  const handlePhoto = async (files) => {
    if (!files?.[0]) return;
    const f = files[0];
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image (JPG/PNG)");
      return;
    }
    const dataUrl = await readAsDataURL(f);
    updateCandidate({ photo: { name: f.name, size: f.size, type: f.type, dataUrl } });
    toast.success("Photograph uploaded");
  };

  // Field-level errors (only shown after user presses Continue)
  const errors = useMemo(() => {
    const e = {};
    if (!c.name || c.name.trim().length < 3) e.name = "Enter your full name (3+ chars)";
    if (!c.photo) e.photo = "Photograph is required";
    if (!c.nationality) e.nationality = REQ;
    if (!c.affidavitRef || c.affidavitRef.trim().length < 3) e.affidavitRef = "Enter your UGC anti-ragging reference no.";
    if (!c.dateOfBirth) e.dateOfBirth = REQ;
    else if (c.dateOfBirth > MAX_DOB) e.dateOfBirth = DOB_REQ;
    if (!c.gender) e.gender = REQ;
    if (!c.bloodGroup) e.bloodGroup = REQ;
    if (!c.address || c.address.trim().length < 3) e.address = REQ;
    if (!c.city) e.city = REQ;
    if (!c.state) e.state = REQ;
    if (!c.pincode) e.pincode = REQ;
    else if (!pincodeValid(c.pincode)) e.pincode = PIN_REQ;
    if (!c.emergencyContactName) e.emergencyContactName = REQ;
    if (!c.emergencyContactPhone) e.emergencyContactPhone = REQ;
    else if (!phoneValid(c.emergencyContactPhone)) e.emergencyContactPhone = PHONE_REQ;
    if (!c.emergencyContactRelation) e.emergencyContactRelation = REQ;
    return e;
  }, [c]);

  const canNext = Object.keys(errors).length === 0;

  const errCls = (key) =>
    showErrors && errors[key] ? "mu-input border-red-400 focus:border-red-500 focus:ring-red-500" : "mu-input";

  const proceed = () => {
    if (!canNext) {
      setShowErrors(true);
      const first = Object.keys(errors)[0];
      toast.error(`Please complete all required fields (${Object.keys(errors).length} pending)`);
      setTimeout(() => {
        const el = document.querySelector(`[data-field="${first}"]`);
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      {/* Accuracy note */}
      <div className="mu-card p-5 bg-blue-50/50 border-blue-100" data-testid="accuracy-note">
        <div className="flex items-start gap-3">
          <IdCard className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
          <p className="text-sm text-[#111] leading-relaxed">
            Enter the details <span className="font-semibold">exactly as they appear on your SSC / CBSE / IB / Cambridge / Any Other certificate</span>, whichever is applicable. The photograph uploaded here will be used for your <span className="font-semibold">ID card generation</span> and printing of your <span className="font-semibold">provisional certificate</span>. All fields on this page are mandatory.
          </p>
        </div>
        <label className="mt-4 flex items-start gap-2 p-3 rounded-xl border border-blue-200 bg-white cursor-pointer">
          <Checkbox
            checked={accuracyConfirmed}
            onCheckedChange={(v) => setAccuracyConfirmed(!!v)}
            className="mt-0.5 data-[state=checked]:bg-[#B60F2A] data-[state=checked]:border-[#B60F2A]"
            data-testid="chk-accuracy-confirm"
          />
          <span className="text-sm text-[#111]">
            I confirm I will enter my name and upload my photograph exactly as required above.
          </span>
        </label>
      </div>

      <div className="mu-card p-6 md:p-8 space-y-6">
        <div data-field="name">
          <label className="mu-label" htmlFor="name">
            Candidate Name (as per 10th certificate)<RequiredStar />
          </label>
          <input
            id="name"
            data-testid="input-candidate-name"
            className={`${errCls("name")} ${!accuracyConfirmed ? "bg-[#F7F7F7] text-[#6B7280] cursor-not-allowed" : ""}`}
            placeholder="e.g. Ananya Iyer"
            value={c.name}
            readOnly={!accuracyConfirmed}
            onFocus={(e) => {
              if (!accuracyConfirmed) {
                e.target.blur();
                promptAccuracyConfirm();
              }
            }}
            onChange={(e) => updateCandidate({ name: e.target.value.toUpperCase() })}
          />
          <ErrText showErrors={showErrors} errors={errors} k="name" />
        </div>

        <div data-field="photo">
          <label className="mu-label">Photograph<RequiredStar /></label>
          <div
            role="button"
            tabIndex={0}
            onClick={() => (accuracyConfirmed ? fileRef.current?.click() : promptAccuracyConfirm())}
            onKeyDown={(e) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              accuracyConfirmed ? fileRef.current?.click() : promptAccuracyConfirm();
            }}
            onDragOver={(e) => { e.preventDefault(); if (accuracyConfirmed) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (!accuracyConfirmed) return promptAccuracyConfirm();
              handlePhoto(e.dataTransfer.files);
            }}
            className={`grid grid-cols-1 sm:grid-cols-[auto_1fr] items-center gap-5 border-2 border-dashed rounded-2xl p-5 transition ${
              !accuracyConfirmed ? "cursor-not-allowed opacity-60 border-[#E5E7EB]"
                : dragOver ? "cursor-pointer border-[#B60F2A] bg-[#FBE7EA]/60"
                : showErrors && errors.photo ? "cursor-pointer border-red-400 bg-red-50/40"
                : "cursor-pointer border-[#E5E7EB] hover:border-[#B60F2A] hover:bg-[#FBE7EA]/30"
            }`}
            data-testid="photo-drop"
          >
            <div className="h-24 w-24 rounded-xl overflow-hidden bg-[#F7F7F7] border border-[#E5E7EB] flex items-center justify-center mx-auto sm:mx-0">
              {c.photo?.dataUrl || c.photo?.file_url ? (
                <img src={c.photo.dataUrl || c.photo.file_url} alt="Candidate" className="w-full h-full object-cover" />
              ) : (
                <User className="h-8 w-8 text-[#9CA3AF]" />
              )}
            </div>
            <div className="text-center sm:text-left">
              <div className="flex items-center gap-2 justify-center sm:justify-start">
                <UploadCloud className="h-4 w-4 text-[#B60F2A]" />
                <span className="text-sm font-medium text-[#111]">
                  {c.photo ? "Replace photograph" : "Drop photo here or click to browse"}
                </span>
              </div>
              <p className="text-xs text-[#9CA3AF] mt-1">Passport-size, JPG/PNG, up to 2 MB. Face clearly visible.</p>
            </div>
            <input ref={fileRef} data-testid="input-photo" type="file" accept="image/*" className="hidden" onChange={(e) => handlePhoto(e.target.files)} />
          </div>
          <ErrText showErrors={showErrors} errors={errors} k="photo" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div data-field="nationality">
            <label className="mu-label" htmlFor="nat">Nationality<RequiredStar /></label>
            <select
              id="nat"
              data-testid="select-nationality"
              className={`${errCls("nationality")} appearance-none pr-9 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2214%22 height=%2214%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%236B7280%22 stroke-width=%222%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-no-repeat bg-[right_0.75rem_center]`}
              value={c.nationality}
              onChange={(e) => updateCandidate({ nationality: e.target.value })}
            >
              <option value="">Select nationality</option>
              {NATIONALITIES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <ErrText showErrors={showErrors} errors={errors} k="nationality" />
          </div>
          <div data-field="affidavitRef">
            <label className="mu-label" htmlFor="aff">
              Anti-Ragging Affidavit Reference No.<RequiredStar />
            </label>
            <input
              id="aff"
              data-testid="input-affidavit-ref"
              className={errCls("affidavitRef")}
              placeholder="e.g. AR12345678"
              value={c.affidavitRef}
              onChange={(e) => updateCandidate({ affidavitRef: e.target.value })}
            />
            <ErrText showErrors={showErrors} errors={errors} k="affidavitRef" />
          </div>
        </div>
      </div>

      {/* UGC instruction card — MOVED BELOW the reference-number input */}
      <div className="mu-card p-6 bg-[#FBE7EA]/40 border-[#FBE7EA]">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-[#B60F2A] mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-[#111]">How to get the Anti-Ragging Reference No.</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              Complete the UGC online affidavit at{" "}
              <a
                href={UGC_UNIVERSITY_INFO.affidavitUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[#B60F2A] hover:underline inline-flex items-center gap-1 font-medium break-all"
              >
                antiragging.in/affidavit_university_form.php <ExternalLink className="h-3 w-3 shrink-0" />
              </a>
              . After submission, a unique reference number is generated — enter it in the field above.
            </p>

            <Dialog>
              <DialogTrigger asChild>
                <button className="mu-btn-secondary mt-4 text-sm" data-testid="btn-view-instructions">
                  View Instructions
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-instructions">
                <DialogHeader>
                  <DialogTitle>UGC Anti-Ragging Affidavit — Instructions</DialogTitle>
                  <DialogDescription>Use this information while filling the online UGC Anti-Ragging Affidavit form.</DialogDescription>
                </DialogHeader>

                <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7] p-4 text-sm">
                  <div className="text-[#111]">
                    Open the affidavit form:{" "}
                    <a href={UGC_UNIVERSITY_INFO.affidavitUrl} target="_blank" rel="noreferrer" className="text-[#B60F2A] hover:underline font-medium break-all">
                      {UGC_UNIVERSITY_INFO.affidavitUrl}
                    </a>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="tiny-label mb-2">Information required for the affidavit</div>
                  <dl className="rounded-xl border border-[#E5E7EB] divide-y divide-[#F3F4F6] overflow-hidden">
                    {[
                      ["Vice Chancellor", UGC_UNIVERSITY_INFO.viceChancellor],
                      ["University Phone No.", UGC_UNIVERSITY_INFO.universityPhone],
                      ["Details of the Course", UGC_UNIVERSITY_INFO.courseType],
                      ["Name of the Course", UGC_UNIVERSITY_INFO.courseName],
                      ["Current Year of Study", UGC_UNIVERSITY_INFO.currentYear],
                      ["Nearest Police Station", UGC_UNIVERSITY_INFO.nearestPoliceStation],
                    ].map(([k, v]) => (
                      <div key={k} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-1 sm:gap-4 px-4 py-3 bg-white">
                        <dt className="text-xs text-[#6B7280] uppercase tracking-wider font-semibold">{k}</dt>
                        <dd className="text-sm font-medium text-[#111]">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="mu-card p-6 md:p-8 space-y-6">
        <div className="flex items-center gap-2">
          <Users2 className="h-4 w-4 text-[#B60F2A]" />
          <h3 className="font-semibold text-[#111]">Personal & Administrative Details</h3>
        </div>
        <p className="text-xs text-[#6B7280] -mt-4">All fields on this page are mandatory.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div data-field="dateOfBirth">
            <label className="mu-label" htmlFor="dob">Date of Birth<RequiredStar /></label>
            <div className="relative">
              {dobFocused && (
                <div className="absolute bottom-full left-0 mb-2 z-10 w-64 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-[#111] shadow-lg" data-testid="dob-hint-card">
                  Enter your date of birth exactly as it appears on your SSC certificate / Passport / Aadhar card.
                </div>
              )}
              <input
                id="dob"
                type="date"
                data-testid="input-dob"
                max={MAX_DOB}
                className={errCls("dateOfBirth")}
                value={c.dateOfBirth || ""}
                onChange={(e) => updateCandidate({ dateOfBirth: e.target.value })}
                onFocus={() => setDobFocused(true)}
                onBlur={() => setDobFocused(false)}
              />
            </div>
            <ErrText showErrors={showErrors} errors={errors} k="dateOfBirth" />
          </div>
          <div data-field="gender">
            <label className="mu-label" htmlFor="gender">Gender<RequiredStar /></label>
            <select id="gender" data-testid="select-gender" className={errCls("gender")} value={c.gender || ""} onChange={(e) => updateCandidate({ gender: e.target.value })}>
              <option value="">Select</option>
              {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <ErrText showErrors={showErrors} errors={errors} k="gender" />
          </div>
          <div data-field="bloodGroup">
            <label className="mu-label" htmlFor="blood">Blood Group<RequiredStar /></label>
            <select id="blood" data-testid="select-blood" className={errCls("bloodGroup")} value={c.bloodGroup || ""} onChange={(e) => updateCandidate({ bloodGroup: e.target.value })}>
              <option value="">Select</option>
              {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <ErrText showErrors={showErrors} errors={errors} k="bloodGroup" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div data-field="pincode">
            <label className="mu-label" htmlFor="pin">
              PIN Code<RequiredStar />
              {pinLoading && <span className="ml-2 text-[10px] font-medium text-[#B60F2A]">Looking up…</span>}
            </label>
            <input
              id="pin"
              data-testid="input-pincode"
              className={errCls("pincode")}
              inputMode="numeric"
              placeholder="6-digit PIN"
              value={c.pincode || ""}
              onChange={(e) => updateCandidate({ pincode: onlyDigits(e.target.value, 10) })}
            />
            <ErrText showErrors={showErrors} errors={errors} k="pincode" />
            <p className="text-[10px] text-[#9CA3AF] mt-1">Enter your PIN — we&apos;ll auto-fill city &amp; state.</p>
          </div>
          <div data-field="city">
            <label className="mu-label" htmlFor="city">City<RequiredStar /></label>
            <input id="city" data-testid="input-city" className={errCls("city")} value={c.city || ""} onChange={(e) => updateCandidate({ city: e.target.value })} />
            <ErrText showErrors={showErrors} errors={errors} k="city" />
          </div>
          <div data-field="state">
            <label className="mu-label" htmlFor="state">State<RequiredStar /></label>
            <input id="state" data-testid="input-state" className={errCls("state")} value={c.state || ""} onChange={(e) => updateCandidate({ state: e.target.value })} />
            <ErrText showErrors={showErrors} errors={errors} k="state" />
          </div>
        </div>
        <div data-field="address">
          <label className="mu-label" htmlFor="addr">Street / Locality Address<RequiredStar /></label>
          <textarea id="addr" data-testid="input-address" rows={2} className={errCls("address")} placeholder="House / street / area" value={c.address || ""} onChange={(e) => updateCandidate({ address: e.target.value })} />
          <ErrText showErrors={showErrors} errors={errors} k="address" />
        </div>

        <div className="pt-2 border-t border-[#F3F4F6]">
          <div className="tiny-label mb-2">Emergency Contact</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div data-field="emergencyContactName">
              <label className="mu-label" htmlFor="ename">Emergency Contact Name<RequiredStar /></label>
              <input id="ename" data-testid="input-emergency-name" className={errCls("emergencyContactName")} value={c.emergencyContactName || ""} onChange={(e) => updateCandidate({ emergencyContactName: e.target.value })} />
              <ErrText showErrors={showErrors} errors={errors} k="emergencyContactName" />
            </div>
            <div data-field="emergencyContactPhone">
              <label className="mu-label" htmlFor="ephone">Emergency Phone<RequiredStar /></label>
              <input
                id="ephone"
                data-testid="input-emergency-phone"
                className={errCls("emergencyContactPhone")}
                inputMode="numeric"
                type="tel"
                placeholder="10-digit number"
                value={c.emergencyContactPhone || ""}
                onChange={(e) => updateCandidate({ emergencyContactPhone: onlyDigits(e.target.value) })}
              />
              <ErrText showErrors={showErrors} errors={errors} k="emergencyContactPhone" />
            </div>
            <div data-field="emergencyContactRelation">
              <label className="mu-label" htmlFor="erel">Relationship<RequiredStar /></label>
              <input id="erel" data-testid="input-emergency-relation" className={errCls("emergencyContactRelation")} placeholder="e.g. Uncle" value={c.emergencyContactRelation || ""} onChange={(e) => updateCandidate({ emergencyContactRelation: e.target.value })} />
              <ErrText showErrors={showErrors} errors={errors} k="emergencyContactRelation" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <button onClick={onBack} className="mu-btn-secondary" data-testid="btn-back-candidate">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {showErrors && !canNext && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" />
            {Object.keys(errors).length} required field{Object.keys(errors).length > 1 ? "s" : ""} still incomplete.
          </div>
        )}
        <button onClick={proceed} className="mu-btn-primary ml-auto" data-testid="btn-next-candidate">
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
